import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBlogBySlug } from '../../store/slices/blogSlice';

const BlogDetailPage = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const dispatch = useAppDispatch();

    const { currentBlog, loading } = useAppSelector((state) => state.blog);

    useEffect(() => {
        if (slug) {
            dispatch(fetchBlogBySlug(slug));
        }
    }, [slug, dispatch]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    if (loading || !currentBlog) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd200]"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header with Back Button */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 bg-[#ffd200] hover:bg-[#e6bd00] text-black px-6 py-2 rounded font-semibold transition-all"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <span>Back</span>
                    </button>
                </div>
            </div>

            {/* Blog Content */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white shadow-sm overflow-hidden">
                    {/* Title & Meta Info */}
                    <div className="p-8 pb-6">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                            {currentBlog.title}
                        </h1>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="font-semibold">{currentBlog.created_by || currentBlog.author || 'Jamie C'}</span>
                            {(currentBlog.published_date || currentBlog.created_at) && (
                                <>
                                    <span>•</span>
                                    <span>{formatDate(currentBlog.published_date || currentBlog.created_at)}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Hero Image */}
                    <div className="w-full bg-gray-100">
                        {currentBlog.image ? (
                            <img 
                                src={`/admin/storage/blogs/${currentBlog.image}`}
                                alt={currentBlog.title}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/hero-image.jpeg';
                                }}
                                className="w-full max-h-[500px] object-cover"
                            />
                        ) : (
                            <div className="py-20 text-center text-gray-400 italic">No image available</div>
                        )}
                    </div>

                    {/* Blog Content */}
                    <div className="p-8">
                        <div className="prose prose-lg max-w-none">
                            {currentBlog.page_content ? (
                                <div 
                                    className="text-gray-700 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: currentBlog.page_content }} 
                                />
                            ) : (
                                <p className="italic text-gray-500">No content available.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetailPage;
